/**
 * @fileoverview ToastContainer Component - Toast Notification Manager
 *
 * This component manages the display of multiple toast notifications.
 * Handles stacking, scrolling, and bulk dismissal of toasts.
 *
 * @description
 * Key features:
 * - Stacks toasts vertically (newest at bottom)
 * - Max 30% viewport height with scroll
 * - "Dismiss All" button when multiple toasts
 *
 * @requires React
 */
import React, { useRef, useEffect } from 'react';
import Toast from './Toast';

/**
 * ToastContainer Component
 *
 * @param {Object} props
 * @param {Array} props.toasts - Array of toast objects {id, message, type, persistent}
 * @param {Function} props.onDismiss - Callback to dismiss a single toast by id
 * @param {Function} props.onDismissAll - Callback to dismiss all toasts
 * @returns {JSX.Element|null}
 */
function ToastContainer({ toasts, onDismiss, onDismissAll }) {
  const scrollRef = useRef(null);

  // Auto-scroll to bottom when new toast is added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [toasts.length]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2 max-w-[90vw] sm:max-w-sm">
      {/* Dismiss All button - only show when multiple toasts */}
      {toasts.length > 1 && (
        <button
          onClick={onDismissAll}
          className="text-xs text-gray-300 hover:text-white bg-gray-900 hover:bg-gray-800 px-3 py-1.5 rounded-lg transition-colors border border-gray-700"
        >
          Dismiss All ({toasts.length})
        </button>
      )}
      {/* Scrollable toast list - max 30% viewport height (newest at bottom) */}
      <div ref={scrollRef} className="flex flex-col gap-2 max-h-[30vh] overflow-y-auto pr-1 w-full">
        {toasts.map((t) => (
          <Toast
            key={t.id}
            message={t.message}
            type={t.type}
            persistent={t.persistent}
            onClose={() => onDismiss(t.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default ToastContainer;
